import {useEffect, useState} from "react";
import {Bell, BellOff} from "lucide-react";
import {toast} from "sonner";

import {CtaButtonDropdown, DropdownItem} from "@/main/react/buttons/CtaButton";
import {ClientFactory, GroupNotificationLevel} from "@/main/common/ClientFactory";
import {getSafeToastMessageFromError} from "@/main/common/ApiUtils";
import {Localizer} from "@/main/react/Types";

type GroupNotificationLevelControlProps = {
    groupPublicId: string;
    localizer: Localizer;
    compact?: boolean;
};

/**
 * Member-only control for the per-group new-post notification level. Lets a member choose whether
 * new discussions in this group notify them. P0 exposes a two-way choice — All / Off; the backend
 * also supports HIGHLIGHTS, wired for P1 once the announcements slice emits a highlight signal.
 *
 * Reads the current level lazily on mount (default ALL) and writes optimistically, rolling back on
 * error. Only rendered for members, so its dropdown items don't need a login gate.
 */
export default function GroupNotificationLevelControl(
    {groupPublicId, localizer, compact = false}: GroupNotificationLevelControlProps,
) {
    const [level, setLevel] = useState<GroupNotificationLevel>("ALL");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let active = true;
        ClientFactory.getGroupNotificationPreference(groupPublicId)
            .then((res) => {
                if (active && res.data?.level) {
                    setLevel(res.data.level);
                }
            })
            .catch(() => {
                // Non-fatal: keep the optimistic default; the control still works on click.
            });
        return () => {
            active = false;
        };
    }, [groupPublicId]);

    const choose = async (next: GroupNotificationLevel) => {
        if (busy || next === level) {
            return;
        }
        const previous = level;
        setBusy(true);
        setLevel(next);
        try {
            await ClientFactory.setGroupNotificationPreference(groupPublicId, next);
            toast.success(localizer("group.notifications.updated"));
        } catch (error) {
            setLevel(previous);
            toast.error(getSafeToastMessageFromError(error, localizer("group.notifications.update_failed")));
        } finally {
            setBusy(false);
        }
    };

    // Same reading as the trigger below (`on`), so the checked row and the announced level can never
    // disagree; a stored HIGHLIGHTS level reads as "All" in both until P1 offers it as its own option.
    const on = level !== "OFF";
    const items: DropdownItem[] = [
        {
            id: "group-notifications-all",
            icon: Bell,
            requiresLogin: false,
            text: localizer("group.notifications.level.all"),
            checked: on,
            onClick: () => choose("ALL"),
        },
        {
            id: "group-notifications-off",
            icon: BellOff,
            requiresLogin: false,
            text: localizer("group.notifications.level.off"),
            checked: !on,
            onClick: () => choose("OFF"),
        },
    ];

    return (
        <div className="inline-flex shrink-0">
            <CtaButtonDropdown
                icon={on ? Bell : BellOff}
                text={null}
                // The name has to MOVE with the state. This control's state is conveyed by nothing but
                // which glyph is drawn (`Bell` vs `BellOff`), so a static name would tell a non-visual
                // user less than silence does: it would assert one of the two levels permanently. It
                // announces the level currently in force, the way a select announces its value, reusing
                // the two catalog keys the menu items already carry rather than minting a third
                // phrasing (AGENTS.md rule 11). Register row `DASHBOARD-1`.
                ariaLabel={localizer(on ? "group.notifications.level.all" : "group.notifications.level.off")}
                playwrightId="group-notification-level-button"
                // `DASHBOARD-104` (A5): the footer siblings' resting treatment. As `ghost` this was the only
                // borderless control in the group card's footer, next to bordered pills, and its state read
                // as a blue fill on a bare glyph. The outline pill is the sibling "Open group"'s variant.
                variant="outline"
                size={compact ? "sm" : "md"}
                buttonActive={on}
                disabledReason={busy ? localizer("group.action_in_progress") : null}
                extraClass="group-notification-level-button min-w-0"
                dropdownItems={items}
            />
        </div>
    );
}
